import * as React from "react";
import {observer} from "mobx-react";
import styled from "styled-components";
import {withAppStores} from "../../../AppStores";
import type {
	Section,
	LinkBasicDto,
	LinkPropertiesUpdatedDto,
	UpdateLinkPropertyRequest,
	XyiconCatalogSettingsModel,
	XyiconCatalogUpdateSettingsDto,
	SpaceDto,
} from "../../../generated/api/base";
import {XyiconFeature, Permission, LinkDirection, LineStyle, ArrowStyle} from "../../../generated/api/base";
import type {TransportLayer} from "../../../data/TransportLayer";
import type {IModel} from "../../../data/models/Model";
import type {AppState} from "../../../data/state/AppState";
import type {FeatureMap} from "../../../data/state/AppStateConstants";
import {featureTitlePlurals, featureTitles} from "../../../data/state/AppStateConstants";
import type {Catalog} from "../../../data/models/Catalog";
import type {Space} from "../../../data/models/Space";
import type {Navigation} from "../../../Navigation";
import {StringUtils} from "../../../utils/data/string/StringUtils";
import {Report} from "../../../data/models/Report";
import {ReactUtils} from "../../utils/ReactUtils";
import {ArrayUtils} from "../../../utils/data/array/ArrayUtils";
import {Spinner} from "../loader/Spinner";
import {TimeUtils} from "../../../utils/TimeUtils";
import type {Field as FieldModel, IFieldAdapter} from "../../../data/models/field/Field";
import type {Xyicon} from "../../../data/models/Xyicon";
import type {Boundary} from "../../../data/models/Boundary";
import {DebugInformation} from "../../../utils/DebugInformation";
import {AppUtils} from "../../../utils/AppUtils";
import {AssignType} from "../../modules/settings/modules/field/FieldForm";
import {XHRLoader} from "../../../utils/loader/XHRLoader";
import {SideBar} from "../../sidebar/SideBar";
import {KeyboardListener} from "../../../utils/interaction/key/KeyboardListener";
import {LookupUtils} from "../../modules/abstract/sidepanel/tabs/details/LookupUtils";
import {getFieldIconAndInheritance} from "../../modules/abstract/sidepanel/tabs/details/field/FieldInputUtils";
import {baseDistance, ELLIPSIS, Flex, FlexCenter, fontSize, fontWeight, VerticalFlex, VerticalFlexStyle} from "../styles/styles";
import InfoIcon from "../icons/circle-information.svg?react";
import LookupFieldIcon from "../icons/arrow-up-right.svg?react";
import CloseIcon from "../icons/xmark.svg?react";
import PinIcon from "../icons/location-pin.svg?react";
import OpenPortfolioIcon from "../icons/arrow-up-right-from-square.svg?react";
import {SpaceCard} from "../modules/spaceeditor/spaceselector/SpaceCard";
import {ToggleContainerStyled, ToggleContainerV5} from "../widgets/ToggleContainerV5/ToggleContainerV5";
import {PortfolioDefaultFieldsV5} from "../abstract/defaultfields/PortfolioDefaultFieldsV5";
import {CatalogDefaultFieldsV5} from "../abstract/defaultfields/CatalogDefaultFieldsV5";
import {SpaceDefaultFieldsV5} from "../abstract/defaultfields/SpaceDefaultFieldsV5";
import {BoundaryDefaultFieldsV5} from "../abstract/defaultfields/BoundaryDefaultFieldsV5";
import {XyiconDefaultFieldsV5} from "../abstract/defaultfields/XyiconDefaultFieldsV5";
import {ReportDefaultFieldsV5} from "../abstract/defaultfields/ReportDefaultFieldsV5";
import {MarkupDefaultFieldsV5} from "../abstract/defaultfields/MarkupDefaultFieldsV5";
import {InitialsV5} from "../widgets/InitialsV5";
import {ClickToEditInputStyled} from "../input/clicktoedit/ClickToEditInputV5";
import {SelectInputStyled, SelectInputV5} from "../input/select/SelectInputV5";
import {IconButtonStyled, IconButtonV5} from "../interaction/IconButtonV5";
import {NavigationEnum} from "../../../Enums";
import {MultiDefaultFieldsV5} from "../abstract/defaultfields/MultiDefaultFieldsV5";
import type {IPropertyDetails} from "../../modules/abstract/sidepanel/tabs/details/field/mass/IMassInput";
import type {BoundarySpaceMap} from "../../../data/models/BoundarySpaceMap";
import type {Markup} from "../../../data/models/Markup";
import {onWorkspaceViewSelect, selectWorkspaceViewTabForFeature} from "../topbar/ViewTabsCommon";
import type {WorkspaceViewType} from "../navigation/views/WorkspaceViewCommon";
import {CreateViewPopup} from "../abstract/view/create/CreateViewPopup";
import type {View} from "../../../data/models/View";
import {CreateSpaceUtils} from "../modules/space/CreateSpaceUtils";
import {ReportHistoryV5} from "../modules/report/ReportHistoryV5";
import AngleDown from "../icons/chevron-down.svg?react";
import SolidLine from "../icons/no-arrow-line.svg?react";
import UniDirectionalArrowLeftIcon from "../icons/uni-directional-arrow-left.svg?react";
import UniDirectionalArrowRightIcon from "../icons/uni-directional-arrow-right.svg?react";
import BiDirectionalArrowIcon from "../icons/bi-directional-arrow.svg?react";
import DashedLineIcon from "../icons/dashed-line.svg?react";
import FilledArrowIcon from "../icons/filled-arrow.svg?react";
import DotArrowIcon from "../icons/dot-arrow.svg?react";
import {ButtonV5} from "../button/ButtonV5";
import {openWorkspaceFromPortfolioModule} from "../modules/ActionBarUtils";
import type {ISectionField} from "../settings/modules/layout/LayoutSettingsV5";
import {colorPalette} from "../styles/colorPalette";
import {HorizontalAlignment} from "../../../utils/dom/DomUtils";
import type {Link} from "../../../data/models/Link";
import type {Xyicon3D} from "../../modules/space/spaceeditor/logic3d/elements3d/Xyicon3D";
import {ColorSelectorV5} from "../colors/ColorSelectorV5";
import type {User} from "../../../data/models/User";
import {DateFormatter} from "../../../utils/format/DateFormatter";
import {Formatter} from "../../../utils/format/Formatter";
import {Debouncer} from "../../../utils/function/Debouncer";
import {ObjectUtils} from "../../../utils/data/ObjectUtils";
import type {Portfolio} from "../../../data/models/Portfolio";
import {doesUserHavePermissionsToSpaceOfItem, extendLocalOrgSearchModel, onLocateClick} from "../topbar/OrganizationWideSearchUtils";
import {BoundarySpaceMapSelectorV5} from "../spaceeditor/BoundarySpaceMapSelectorV5";
import {isHiddenViewColumnV5} from "../../../data/models/ViewUtils";
import {FeatureService} from "../../../data/services/FeatureService";
import ReportSharingContainer from "../sharing/ReportSharingContainer";
import {DetailsContentV5HeaderStyled} from "./DetailsContentV5HeaderStyled";
import {FieldV5} from "./FieldV5";
import {MultiSelectInputV5} from "./datatypes/multi/MultiSelectInputV5";
import {MassFieldLookupInputV5} from "./datatypes/mass/MassFieldLookupInputV5";
import {SingleFieldInputV5} from "./datatypes/SingleFieldInputV5";
import type {ISelectSliderOption, ISelectSliderRow} from "./SelectSliderV5";
import {SelectSliderV5} from "./SelectSliderV5";
import {EmptyListViewV5} from "./EmptyListViewV5";
import {LinksSectionV5} from "./LinksSectionV5";
import {PortsSectionV5} from "./PortsSectionV5";
import {DocumentSectionV5} from "./DocumentSectionV5";
import {PropertiesSectionV5} from "./PropertiesSectionV5";
import {PhoneInputStyled} from "./datatypes/PhoneInputV5";
import {MassFieldV5} from "./datatypes/mass/MassFieldV5";
import {FieldElementsContainerStyled} from "./Field.styled";
import {PropagatedFieldInDetailsContent} from "./datatypes/SingleFieldInput.styled";
import {MultiSelectInputStyled} from "./datatypes/multi/MultiSelect.styled";
import {ReportScopeWidgetV5} from "./ReportScopeWidgetV5";

interface IDetailsContentV5Props<T extends IModel = IModel> {
	readonly items: T[];
	readonly feature: XyiconFeature;
	readonly features: XyiconFeature[];
	readonly isPortTemplateEditorOpen: boolean;
	readonly noInitials?: boolean;
	readonly appState?: AppState;
	readonly transport?: TransportLayer;
	readonly navigation?: Navigation;
	readonly insideDetailsContainer?: boolean;
	readonly onSelect?: (items: T[]) => void;
	readonly onFocus?: (items: T[]) => void;
	readonly setPortTemplateEditorOpen: (value: boolean) => void;
	readonly onCatalogIconEditClick?: (catalog: Catalog) => void;
	readonly closeWideSearchPanel?: () => void;
	readonly onCloseOverlayedDetaislPanel?: () => void;
	readonly additionalHeaderControls?: (items: IModel[]) => React.ReactNode; // When we want to pass module specific controls in header
}

interface IDetailsContentV5State {
	openFieldRefId: string;
	isSpaceListLoading: boolean;
	focusedFieldParentSectionLabel: string;
	createViewPopupType: WorkspaceViewType | null;
	headerIsAnchored: boolean;
	headerMinimized: boolean;
	detailedBoundary: Boundary | null;
}

interface ILinkThumbnailAndRedIdDetails {
	objectA: {
		thumbnail: string;
		refId: string;
	};
	objectB: {
		thumbnail: string;
		refId: string;
	};
}

interface ILastModifiedDetails {
	lastModifiedByUser: string;
	lastModifiedAt: string;
}

const defaultFieldComponents: FeatureMap<React.ComponentClass<{item: IModel}> | React.FunctionComponent<{item: IModel}>> = {
	[XyiconFeature.Portfolio]: PortfolioDefaultFieldsV5,
	[XyiconFeature.XyiconCatalog]: CatalogDefaultFieldsV5,
	[XyiconFeature.Space]: SpaceDefaultFieldsV5,
	[XyiconFeature.Boundary]: BoundaryDefaultFieldsV5,
	[XyiconFeature.Xyicon]: XyiconDefaultFieldsV5,
	[XyiconFeature.Report]: ReportDefaultFieldsV5,
	[XyiconFeature.Markup]: MarkupDefaultFieldsV5,
};

const lookupPrefix = "lookup-";
const logId: string = "Rendering DetailsContent UI";

@withAppStores("appState", "transport", "navigation")
@observer
export class DetailsContentV5<T extends IModel = IModel> extends React.Component<IDetailsContentV5Props<T>, IDetailsContentV5State> {
	private readonly _ref = React.createRef<HTMLDivElement>();
	private readonly _linkLineColorSelectorDebouncer = new Debouncer(500);
	private readonly _refForIntersectionObserver = React.createRef<HTMLDivElement>();
	private readonly _sectionFields: string[] = [];
	private _savedPortfolioId: string = "";
	private _loadedSpacesPortfolioId: string = "";
	private _linkPropertiesUpdateHandler: ((data: LinkBasicDto) => void) | null = null;
	private readonly _sectionFieldsRefArray: {label: string; ref: React.RefObject<ToggleContainerV5>}[] = [];
	private _onMouseDownTargetElement: Element = null;
	private readonly _intersectionObserver = new IntersectionObserver((entries) => {
		this.setState({headerIsAnchored: !entries[0].isIntersecting});
	});
	private static readonly LINE_WIDTH_OPTIONS = [4, 5, 6, 7, 8];
	private static readonly ICON_STYLE = {width: "16px", height: "16px"};
	private static readonly DEFAULT_LINK_PROPERTIES = {
		direction: LinkDirection.Both,
		settings: {
			lineColor: "#1E88E5",
			lineWidth: 4,
			arrowStyle: ArrowStyle.Filled,
			lineStyle: LineStyle.Solid,
		},
	};

	public _assignedTypes: ISelectSliderOption[] = [
		{
			id: AssignType.Unassigned,
			label: AssignType[AssignType.Unassigned],
		},
		{
			id: AssignType.Assigned,
			label: AssignType[AssignType.Assigned],
		},
	];

	public static getDerivedStateFromProps(props: IDetailsContentV5Props, state: IDetailsContentV5State) {
		const items = DetailsContentV5.getSanitizedItems(props.items ?? []);

		if (items?.length < 2 || !DetailsContentV5.getFieldRefIds(props).includes(state.openFieldRefId)) {
			return {
				openFieldRefId: "",
			} as IDetailsContentV5State;
		}

		return null;
	}

	private static getFieldRefIds(props: IDetailsContentV5Props, feature?: XyiconFeature) {
		const {items, appState} = props;

		const fieldRefIdsForType: string[] = [];

		for (const item of items) {
			if (item?.typeId) {
				const newRefIds = appState.actions.getFieldRefIdsForType(item.typeId, feature ?? item.ownFeature);

				for (const refId of newRefIds) {
					if (!fieldRefIdsForType.includes(refId)) {
						fieldRefIdsForType.push(refId);
					}
					if (LookupUtils.isLookupField(appState.actions.getFieldByRefId(refId) as FieldModel, item, appState.actions)) {
						fieldRefIdsForType.push(`${lookupPrefix}${refId}`);
					}
				}
			}
		}

		return fieldRefIdsForType;
	}

	private static getItemIds(items: IModel[]) {
		return DetailsContentV5.getSanitizedItems(items)
			.map((item) => item.id)
			.toSorted(StringUtils.sortIgnoreCase)
			.join();
	}

	constructor(props: IDetailsContentV5Props<T>) {
		super(props);
		this.state = {
			isSpaceListLoading: true,
			openFieldRefId: "",
			focusedFieldParentSectionLabel: "",
			createViewPopupType: null,
			headerIsAnchored: false,
			headerMinimized: true,
			detailedBoundary: null,
		};
	}

	private static getSanitizedItems(items: IModel[]): IModel[] {
		return ArrayUtils.removeDuplicates(
			items.map((item) => ((item as BoundarySpaceMap)?.isBoundarySpaceMap ? (item as BoundarySpaceMap).parent : item)),
		);
	}

	private get items(): T[] {
		return DetailsContentV5.getSanitizedItems(this.props.items as IModel[]) as T[];
	}

	private renderDefaultFields() {
		const DefaultFields = defaultFieldComponents[this.props.feature];

		if (DefaultFields) {
			const item = this.items[0];

			return <DefaultFields item={item} />;
		}
		return null;
	}

	private renderInitialComponent() {
		const {appState, onCatalogIconEditClick} = this.props;
		const {items} = this;

		const item = items[0];
		const itemType = appState.actions.getTypeById(item?.typeId);
		const color = `${itemType?.settings.color.hex || "FFFFFF"}`;

		return (
			<InitialsV5
				item={item}
				backgroundColor={color}
				name={itemType?.name}
				onCatalogIconEditClick={onCatalogIconEditClick}
			/>
		);
	}

	private onSelectPropagatedSource = (model: IModel) => {
		if (this.props.features.includes(model.ownFeature)) {
			this.props.onSelect?.([model] as T[]);
		}
	};

	private onOpenMultiInput = (refId: string) => {
		this.setState({openFieldRefId: refId});
	};

	private shouldFieldBeDisabledByPermission(field: IFieldAdapter) {
		const {appState} = this.props;
		const {items} = this;
		const perm = appState.actions.getFieldPermission(field, items);

		return perm === Permission.None || perm === Permission.View;
	}

	private getLayoutSections() {
		const {feature, features} = this.props;
		const {items} = this;

		if (items.length > 1 && features.length > 1) {
			return [...this.getSections(XyiconFeature.Boundary), ...this.getSections(XyiconFeature.Xyicon)];
		} else {
			return this.getSections(feature);
		}
	}

	private getSections(feature: XyiconFeature) {
		const layout = this.props.appState.layouts[feature];
		const sections: Section[] = layout ? layout.sections : [];

		return sections.map((section) => {
			return {
				feature,
				section,
			};
		});
	}

	private getAssignedList(feature: XyiconFeature) {
		const {appState} = this.props;
		const {items} = this;

		if (!items[0] || !appState.user?.isAdmin || this.props.feature !== XyiconFeature.XyiconCatalog) {
			return [];
		}

		return DetailsContentV5.getFieldRefIds(this.props, feature).filter((f) => this.props.appState.actions.getFieldByRefId(f)?.isAssignedByModel);
	}

	private getSliderRowsFromFieldList(list: string[], items: T[], feature: XyiconFeature): ISelectSliderRow[] {
		const {appState} = this.props;
		const catalogs = items as IModel[] as Catalog[];
		const dataArray = catalogs.map((cat) => {
			const visibleFields = (feature === XyiconFeature.Xyicon ? cat.xyiconVisibleFields : cat.catalogVisibleFields) as string[];
			const assignByModelList = appState.actions
				.getFieldRefIdsForType(cat.typeId, feature)
				.filter((f) => appState.actions.getFieldByRefId(f).isAssignedByModel);

			return {visibleFields, assignByModelList};
		});

		return list.map((refId) => {
			const field = appState.actions.getFieldByRefId(refId);
			const isFieldUnassignedToAnyItem = dataArray.some((el) => el.assignByModelList.includes(refId) && !el.visibleFields.includes(refId));

			return {
				label: field.name,
				value: isFieldUnassignedToAnyItem ? AssignType.Unassigned : AssignType.Assigned,
				disabledOptionsList: [] as ISelectSliderOption[],
			};
		});
	}

	private onSliderChange = async (fieldNames: string[], value: AssignType, feature: XyiconFeature) => {
		const {appState} = this.props;
		const {items} = this;
		const fieldRefIdsToChange = fieldNames.map((rowKey) => appState.actions.getFieldByName(feature, rowKey).refId);
		const updatedXyiconCatalogs: XyiconCatalogSettingsModel[] = [];

		items.forEach((item: IModel) => {
			fieldRefIdsToChange.forEach((fieldRefId) => {
				const visibleFields =
					feature === XyiconFeature.XyiconCatalog ? (item as Catalog).catalogVisibleFields : (item as Catalog).xyiconVisibleFields;
				const assignByModelList = appState.actions
					.getFieldRefIdsForType(item.typeId, feature)
					.filter((f) => appState.actions.getFieldByRefId(f).isAssignedByModel);

				if (assignByModelList.includes(fieldRefId)) {
					if (value === AssignType.Unassigned) {
						ArrayUtils.removeMutable(visibleFields, fieldRefId);
					} else {
						ArrayUtils.addMutable(visibleFields, fieldRefId);
					}
				}
			});

			updatedXyiconCatalogs.push({xyiconCatalogID: item.id, settings: (item as Catalog).settings});
		});

		const {result, error} = await this.props.transport.requestForOrganization<XyiconCatalogUpdateSettingsDto>({
			url: "xyiconcatalogs/updatesettings",
			method: XHRLoader.METHOD_POST,
			params: {
				updatedXyiconCatalogs,
			},
		});

		if (error) {
			console.warn(error);
		}
	};

	private getProperties(item: Xyicon | Boundary) {
		const properties: IPropertyDetails[] = [];

		properties.push({
			name: "Position",
			measure: true,
			parts: [
				{
					name: "px",
					label: "X",
					ref: "x",
				},
				{
					name: "py",
					label: "Y",
					ref: "y",
				},
				{
					name: "pz",
					label: "Z",
					ref: "z",
				},
			],
		});

		if (item.ownFeature === XyiconFeature.Boundary) {
			properties.push(
				{
					name: "Dimensions",
					measure: true,
					parts: [
						{
							name: "dx",
							label: "X",
							ref: "dimensionX",
						},
						{
							name: "dy",
							label: "Y",
							ref: "dimensionY",
						},
						{
							name: "dz",
							label: "Z",
							ref: "dimensionZ",
						},
					],
				},
				{
					name: "Area",
					measure: false,
					parts: [],
				},
			);
		}

		properties.push({
			name: "Rotation",
			measure: false,
			parts: [
				{
					name: "o",
					label: "Z",
					ref: "orientation",
				},
			],
		});

		return properties;
	}

	private onLookupLinkedFieldsClick = (value: string) => {
		this.props.appState.selectedFieldInputRefId = value || "";
	};

	private renderFields() {
		const {appState, feature, transport, closeWideSearchPanel} = this.props;
		const {items} = this;
		const {openFieldRefId, focusedFieldParentSectionLabel} = this.state;
		const actions = appState.actions;
		const firstItem = items[0];
		const multiSelection = items.length > 1;
		const fieldRefIdsForType = DetailsContentV5.getFieldRefIds(this.props);
		const sections = this.getLayoutSections();

		this._sectionFields.length = 0;
		this._sectionFieldsRefArray.length = 0;

		return sections.map((sectionData, index) => {
			const section = sectionData.section;
			const sectionFields = section.fields.filter((sectionField: ISectionField) => {
				const field = actions.getFieldByRefId(sectionField.id);

				if (!field) {
					return false;
				}

				const isDefault = field.default;
				const hasPermission = isDefault ? true : actions.getFieldPermission(field, items) !== Permission.None;

				if (hasPermission) {
					if (isHiddenViewColumnV5(field.refId)) {
						return false;
					}

					const isMappedToType = fieldRefIdsForType.includes(field.refId);

					if (multiSelection) {
						if (isDefault || field.feature !== sectionData.feature || field.hasFormula) {
							return false;
						}
						return isMappedToType && items.some((item) => !actions.isFieldHiddenByMasking(item, field));
					} else {
						if (!isDefault && field.feature === feature) {
							return (
								(isMappedToType && !actions.isFieldHiddenByMasking(firstItem, field)) || actions.getDynamicFieldPropagations(firstItem, field)?.length
							);
						} else {
							return actions.isFieldShownForFeature(field, feature) && (isDefault || actions.getFieldPropagations(firstItem, field)?.length);
						}
					}
				} else {
					return false;
				}
			});

			if (sectionFields.length > 0) {
				const collapsed = focusedFieldParentSectionLabel === section.label && !this.getSectionFieldParentSectionStatusByLabel(section.label);
				const ref = React.createRef<ToggleContainerV5>();

				this._sectionFieldsRefArray.push({label: section.label, ref});

				return (
					<ToggleContainerV5
						key={`${items.map((item) => item?.id).join("_")}_${section.label}_${index}`}
						title={section.label}
						saveStateToLocalStorage={true}
						className={ReactUtils.cls("FieldSection", {collapsed})}
						ref={ref}
					>
						{sectionFields.map((sectionField: ISectionField) => {
							const field = actions.getFieldByRefId(sectionField.id);
							const elements: React.ReactElement[] = [];
							const lookupLinkFields: T[] = [];
							const notLookupLinkFields: T[] = [];
							const shouldFieldBeDisabledByPermission = this.shouldFieldBeDisabledByPermission(field);

							items.forEach((item) => {
								LookupUtils.isLookupField(field, item, actions) ? lookupLinkFields.push(item) : notLookupLinkFields.push(item);
							});

							if (lookupLinkFields.length > 0) {
								const lookupFieldOptions = LookupUtils.getLookupFieldOptions(appState, field, firstItem);
								const selectedLookupFieldOptions = LookupUtils.getSelectedLookupFieldOptions(appState, lookupFieldOptions, firstItem);
								const prefixedRefId = `${lookupPrefix}${field.refId}`;
								const onChange = LookupUtils.getOnChangeForLookup(transport, appState, lookupFieldOptions, firstItem);

								if (!shouldFieldBeDisabledByPermission && !field.hasFormula && !field.default && field.feature === feature) {
									ArrayUtils.addMutable(this._sectionFields, field.refId);
								}

								elements.push(
									!multiSelection ? (
										<FieldV5
											key={field.refId + field.name}
											label={field.name}
											disabled={shouldFieldBeDisabledByPermission}
											noWrap={false}
											icons={{preLabelIcon: LookupFieldIcon}}
											tooltips={{
												preLabelIconTooltip:
													"This field's value is inherited from a linked object. A lookup field allows you to create or break links from within it.",
											}}
											className={ReactUtils.cls("lookup", {calculated: field.hasFormula})}
										>
											<MultiSelectInputV5
												key={field.refId}
												options={lookupFieldOptions}
												selected={selectedLookupFieldOptions}
												renderOptionWhenClosed={(obj) => (
													<div className="hbox alignCenter">
														{obj.value}&nbsp;<span style={{color: "#A0A0A0"}}>({obj.xyicon.refId})</span>
													</div>
												)}
												onChange={onChange}
												focused={false}
												onClick={() => this.onLookupLinkedFieldsClick(field.refId)}
												disabled={shouldFieldBeDisabledByPermission}
											/>
										</FieldV5>
									) : (
										<MassFieldLookupInputV5
											key={prefixedRefId}
											field={field}
											prefixedRefId={prefixedRefId}
											items={lookupLinkFields}
											open={openFieldRefId === prefixedRefId}
											onOpen={this.onOpenMultiInput}
											selectableOptions={lookupFieldOptions}
											selectedOptions={selectedLookupFieldOptions}
										/>
									),
								);
							}

							if (notLookupLinkFields.length > 0) {
								const shouldFieldBeDisabledByPermission = this.shouldFieldBeDisabledByPermission(field);
								const {icon, isXyiconXyiconLink, isInheritedFromBoundary} =
									!multiSelection && getFieldIconAndInheritance(field, firstItem, sectionData.feature, actions);

								const shouldMassInputBeDisabled =
									multiSelection &&
									(lookupLinkFields.length > 0
										? actions.getFieldPermission(field, notLookupLinkFields) < Permission.Update
										: shouldFieldBeDisabledByPermission);

								if (!shouldFieldBeDisabledByPermission && !field.hasFormula && !field.default && field.feature === feature) {
									ArrayUtils.addMutable(this._sectionFields, field.refId);
								}

								elements.push(
									multiSelection ? (
										<MassFieldV5
											key={field.refId}
											field={field}
											items={notLookupLinkFields}
											icon={icon}
											disabled={shouldMassInputBeDisabled}
										/>
									) : (
										<SingleFieldInputV5
											key={field.refId}
											item={firstItem}
											field={field}
											className={ReactUtils.cls({inherited: icon === "dol-field"})}
											fieldRefIdsForType={fieldRefIdsForType}
											noWrap={sectionField.noWrap}
											feature={feature}
											onSelectPropagatedSource={this.onSelectPropagatedSource}
											disabled={this.shouldFieldBeDisabledByPermission(field)}
											icon={icon}
											isXyiconXyiconLink={isXyiconXyiconLink}
											isInheritedFromBoundary={isInheritedFromBoundary}
											closeWideSearchPanel={closeWideSearchPanel}
										/>
									),
								);
							}

							return elements;
						})}
					</ToggleContainerV5>
				);
			}
			return null;
		});
	}

	private getFieldInputRefId = (offset: number): string => {
		const {appState} = this.props;

		let index = this._sectionFields.indexOf(appState.selectedFieldInputRefId) + offset;

		if (index === this._sectionFields.length) {
			index = 0;
		} else if (index === -1) {
			index = this._sectionFields.length - 1;
		}

		if (appState.selectedFieldInputRefId) {
			return this._sectionFields.at(index) || "";
		}
	};

	private getSectionFieldParentSectionByRefId = (refId: string): Section => {
		const sections = this.getLayoutSections();

		return sections.find((section) => section.section.fields.find((f) => f.id === refId)).section;
	};

	private getSectionFieldParentSectionStatusByLabel = (label: string): boolean => {
		return (
			localStorage.getItem(`srv4-org-${this.props.appState.organizationIdByAuthData}-feature-${SideBar.activeNav}-section-${label}-state`) === "true"
		);
	};

	private onKeyDown = (e: KeyboardEvent) => {
		const {appState} = this.props;
		const {focusedFieldParentSectionLabel} = this.state;
		const sections = this.getLayoutSections();
		const isTargetGeoLatInput = (e.target as HTMLInputElement).classList.contains("geoLat");

		let targetElement: Element = e.target as Element;

		const realParentId = targetElement.parentElement.dataset?.realparentid;

		if (realParentId) {
			targetElement = document.getElementById(realParentId);
		}

		const isTargetInDetailsContainer = document.querySelector(".DetailsContainer")?.contains(targetElement);

		if (
			!!this.props.insideDetailsContainer === isTargetInDetailsContainer &&
			isTargetInDetailsContainer === appState.isDetailsContainerOpened &&
			!isTargetGeoLatInput &&
			(!!appState.selectedFieldInputRefId || focusedFieldParentSectionLabel !== "")
		) {
			AppUtils.disableScrolling(false);

			let nextFieldInputRefId = "";
			let isOffsetNegative = 1;
			let focusedFieldParentSectionLabelVar = "";

			if (e.key === KeyboardListener.KEY_TAB) {
				e.preventDefault();

				if (e.shiftKey) {
					isOffsetNegative *= -1;
				}

				nextFieldInputRefId = this.getFieldInputRefId(isOffsetNegative);

				if (focusedFieldParentSectionLabel !== "") {
					const nextSectionFields = sections.at(
						sections.indexOf(sections.find((s) => s.section.label === focusedFieldParentSectionLabel)) + isOffsetNegative,
					).section.fields;

					nextFieldInputRefId = nextSectionFields[nextSectionFields.length - 2].id;
				} else {
					const nextFieldParentSection = this.getSectionFieldParentSectionByRefId(nextFieldInputRefId);
					const nextFieldParentSectionLabel = nextFieldParentSection.label;
					const isNextFieldsParentSectionOpened = this.getSectionFieldParentSectionStatusByLabel(nextFieldParentSectionLabel);

					if (!isNextFieldsParentSectionOpened) {
						focusedFieldParentSectionLabelVar = nextFieldParentSectionLabel;
						nextFieldInputRefId = "";
					}
				}

				appState.selectedFieldInputRefId = nextFieldInputRefId;
			} else if ((e.code === KeyboardListener.CODE_SPACE || e.key === KeyboardListener.KEY_ENTER) && focusedFieldParentSectionLabel !== "") {
				e.preventDefault();

				this._sectionFieldsRefArray.find((s) => s.label === focusedFieldParentSectionLabel)?.ref.current.onToggleOpen();

				nextFieldInputRefId = sections.find((s) => s.section.label === focusedFieldParentSectionLabel).section.fields[0].id;

				appState.selectedFieldInputRefId = nextFieldInputRefId;
			}

			this.setState({
				focusedFieldParentSectionLabel: focusedFieldParentSectionLabelVar,
			});
		}
	};

	private onMouseDown = (e: MouseEvent) => {
		this._onMouseDownTargetElement = e.target as Element;
	};

	private onDocumentClick = (e: MouseEvent) => {
		const currentlyEditedBasicInput = document.querySelector(`.Field ${ClickToEditInputStyled} .field-input-container input`);
		const currentlyEditedPhoneTextInput = document.querySelector(`.Field ${ClickToEditInputStyled} ${PhoneInputStyled} input`);
		const currentlyEditedPhoneSelectInput = document.querySelector(`.Field ${ClickToEditInputStyled} ${PhoneInputStyled} ${SelectInputStyled}`);
		const currentlyEditedFieldInput = [currentlyEditedBasicInput, currentlyEditedPhoneTextInput, currentlyEditedPhoneSelectInput];
		const eventTargetInModalContainer = this.props.appState.app.modalContainer?.contains(e.target as Element);
		const singleSelectClick = this._onMouseDownTargetElement?.className === `${SelectInputStyled}`;
		const otherFieldClickOut =
			this._onMouseDownTargetElement === e.target && currentlyEditedFieldInput.includes(e.currentTarget as unknown as Element);

		if (e.target instanceof Element && !eventTargetInModalContainer && (otherFieldClickOut || singleSelectClick)) {
			this.props.appState.selectedFieldInputRefId = "";
		}
	};

	private get isReportEditable() {
		const {appState} = this.props;
		const report = this.items[0] as IModel as Report;
		return appState.user.isAdmin && Report.getPermission(report, appState) >= Permission.Update;
	}

	private onPortfolioChange = async () => {
		this.props.transport.services.feature.refreshList(XyiconFeature.Link);
		this.setState({isSpaceListLoading: true});

		try {
			await this.loadSpacesForSelectedPortfolio();
		} catch (error) {
			console.warn(error);
		}

		this.setState({isSpaceListLoading: false});
		this._savedPortfolioId = this.props.appState.activePortfolioId;
	};

	private onHashChange = (): void => {
		if (this.props.appState.activePortfolioId && this.props.appState.activePortfolioId !== this._savedPortfolioId) {
			this.onPortfolioChange();
		}
	};

	private onSpaceClick = (space: Space) => {
		const {transport, navigation} = this.props;
		const portfolioMaybe = transport.appState.actions.getPortfolioById(space.portfolioId);

		selectWorkspaceViewTabForFeature(transport.appState, XyiconFeature.SpaceEditor, true);

		navigation.goApp({
			mainNav: NavigationEnum.SPACE,
			portfolioRefId: portfolioMaybe?.refId,
			subNavs: [space.refId],
		});
	};

	private getSelectedPortfolioFromDetails(): Portfolio | null {
		if (this.props.items.length === 1 && this.props.items[0].ownFeature === XyiconFeature.Portfolio) {
			return this.props.items[0] as IModel as Portfolio;
		}

		return null;
	}

	private loadSpacesForSelectedPortfolio = async () => {
		const selectedPortfolio = this.getSelectedPortfolioFromDetails();
		if (!selectedPortfolio) {
			return;
		}

		await this.props.transport.services.feature.refreshList(XyiconFeature.Space, true, selectedPortfolio.id);
		this._loadedSpacesPortfolioId = selectedPortfolio.id;
	};

	private closeCreateViewPopup = () => {
		this.setState({
			createViewPopupType: null,
		});
	};

	private onAddSpaceClick = async () => {
		const {appState, items} = this.props;

		appState.app.navigation.goApp({
			portfolioRefId: items[0]?.refId,
			mainNav: NavigationEnum.SPACES,
		});

		await TimeUtils.wait(1000);
		CreateSpaceUtils.triggerSpaceCreatePopup();
	};

	private getHeaderTitle = () => {
		const {items} = this;
		const firstItem = items[0];

		if (items.length > 1) {
			return `${items.length} ${items.every((i) => i.ownFeature === firstItem.ownFeature) ? featureTitlePlurals[firstItem.ownFeature] : "Objects"} selected`;
		} else if (items.length === 1) {
			return "Details";
		}
	};

	private readonly onLocateSelectedItemInSpaceEditor = (view: View) => {
		const {items} = this;
		const firstItem = items[0];

		onWorkspaceViewSelect(view, true);
		this.props.appState.actions.navigateToSpaceItem(firstItem as unknown as Boundary | BoundarySpaceMap | Markup | Xyicon);
	};

	private readonly onShowBoundarySpaceMaps = (boundary: Boundary) => {
		this.setState({
			detailedBoundary: boundary,
		});
	};

	private readonly onHideBoundarySpaceMaps = () => {
		this.setState({
			detailedBoundary: null,
		});
	};

	private readonly onOpenInSpaceEditorClick = () => {
		const {appState, items} = this.props;
		const item = items[0];

		return onLocateClick(item as unknown as Boundary | Markup | Xyicon, appState, this.onShowBoundarySpaceMaps);
	};

	private readonly getLinkThumbnailAndRedIdDetails = (item: Link): ILinkThumbnailAndRedIdDetails => {
		const {spaceViewRenderer} = this.props.appState.app;
		const {objectAID, objectBID} = item;

		return {
			objectA: {
				thumbnail: (spaceViewRenderer.xyiconManager.getItemById(objectAID) as Xyicon3D)?.modelData.thumbnail,
				refId: (spaceViewRenderer.xyiconManager.getItemById(objectAID) as Xyicon3D)?.modelData.refId,
			},
			objectB: {
				thumbnail: (spaceViewRenderer.xyiconManager.getItemById(objectBID) as Xyicon3D)?.modelData.thumbnail,
				refId: (spaceViewRenderer.xyiconManager.getItemById(objectBID) as Xyicon3D)?.modelData.refId,
			},
		};
	};

	private readonly lineWidthIconOptions = DetailsContentV5.LINE_WIDTH_OPTIONS.map((width) => ({
		label: <span className="lineWidthLabel">{width}</span>,
		value: width,
		onClick: () => this.updateLinkProperties({settings: {lineWidth: width}}),
	}));

	private readonly lineStyleIconOptions = [
		{
			label: LineStyle.Solid,
			value: LineStyle.Solid,
			icon: (
				<LineStyleOptionsStyled>
					<SolidLine />
					<span className="lineStyleLabel">Solid</span>
				</LineStyleOptionsStyled>
			),
			onClick: () => this.updateLinkProperties({settings: {lineStyle: LineStyle.Solid}}),
		},
		{
			label: LineStyle.Dashed,
			value: LineStyle.Dashed,
			icon: (
				<LineStyleOptionsStyled>
					<DashedLineIcon />
					<span className="lineStyleLabel">Dashed</span>
				</LineStyleOptionsStyled>
			),
			onClick: () => this.updateLinkProperties({settings: {lineStyle: LineStyle.Dashed}}),
		},
	];

	private readonly arrowStyleIconOptions = [
		{
			label: ArrowStyle.Dotted,
			value: ArrowStyle.Dotted,
			icon: (
				<LineStyleOptionsStyled>
					<DotArrowIcon />
					<span className="lineStyleLabel">Dot</span>
				</LineStyleOptionsStyled>
			),
			onClick: () => this.updateLinkProperties({settings: {arrowStyle: ArrowStyle.Dotted}}),
		},
		{
			label: ArrowStyle.Filled,
			value: ArrowStyle.Filled,
			icon: (
				<LineStyleOptionsStyled>
					<FilledArrowIcon />
					<span className="lineStyleLabel">Filled</span>
				</LineStyleOptionsStyled>
			),
			onClick: () => this.updateLinkProperties({settings: {arrowStyle: ArrowStyle.Filled}}),
		},
		{
			label: ArrowStyle.Arrow,
			value: ArrowStyle.Arrow,
			icon: (
				<LineStyleOptionsStyled>
					<UniDirectionalArrowRightIcon />
					<span className="lineStyleLabel">Arrow</span>
				</LineStyleOptionsStyled>
			),
			onClick: () => this.updateLinkProperties({settings: {arrowStyle: ArrowStyle.Arrow}}),
		},
	];

	private readonly directionalityIconOptions = [
		{
			label: LinkDirection.Both,
			value: LinkDirection.Both,
			icon: <BiDirectionalArrowIcon style={{width: "18px", height: "18px"}} />,
			onClick: () => this.updateLinkProperties({direction: LinkDirection.Both}),
		},
		{
			label: LinkDirection.AtoB,
			value: LinkDirection.AtoB,
			icon: <UniDirectionalArrowRightIcon style={DetailsContentV5.ICON_STYLE} />,
			onClick: () => this.updateLinkProperties({direction: LinkDirection.AtoB}),
		},
		{
			label: LinkDirection.BtoA,
			value: LinkDirection.BtoA,
			icon: <UniDirectionalArrowLeftIcon style={DetailsContentV5.ICON_STYLE} />,
			onClick: () => this.updateLinkProperties({direction: LinkDirection.BtoA}),
		},
		{
			label: LinkDirection.None,
			value: LinkDirection.None,
			icon: <SolidLine style={DetailsContentV5.ICON_STYLE} />,
			onClick: () => this.updateLinkProperties({direction: LinkDirection.None}),
		},
	];

	private readonly updateLinkProperties = (updates: {
		direction?: LinkDirection;
		settings?: Partial<{lineColor: string; lineWidth: number; arrowStyle: ArrowStyle; lineStyle: LineStyle}>;
	}) => {
		const {items, transport, appState} = this.props;
		const {spaceViewRenderer} = appState.app;
		const firstItem = items[0] as IModel as Link;

		if (!firstItem || firstItem.ownFeature !== XyiconFeature.Link) {
			return;
		}

		try {
			const currentProperties = this.getCurrentLinkProperties();

			const updatedLinkProperties = {
				direction: updates.direction ?? currentProperties.direction,
				settings: {
					...currentProperties.settings,
					...updates.settings,
				},
			};

			if (firstItem.data.linkProperties) {
				firstItem.data.linkProperties.direction = updatedLinkProperties.direction;
				if (!firstItem.data.linkProperties.settings) {
					firstItem.data.linkProperties.settings = {};
				}
				firstItem.data.linkProperties.settings = {
					...firstItem.data.linkProperties.settings,
					...updatedLinkProperties.settings,
				};
			}

			spaceViewRenderer.spaceItemController.linkLineManager.updateLinkProperties(firstItem.id, updatedLinkProperties);
			spaceViewRenderer.needsRender = true;
			this.forceUpdate();

			const params: UpdateLinkPropertyRequest = {
				linkID: firstItem.id,
				linkProperties: updatedLinkProperties,
			};

			this._linkLineColorSelectorDebouncer.debounce(async () => {
				const {result, error} = await transport.requestForOrganization<LinkPropertiesUpdatedDto>({
					url: "links/updateproperties",
					method: XHRLoader.METHOD_PUT,
					params: params,
				});

				if (error) {
					console.error("Failed to update link properties:", error);
					return;
				}

				if (result) {
					const resolvedLinkProperties = result.linkProperties
						? {
								direction: result.linkProperties.direction ?? updatedLinkProperties.direction,
								settings: {
									...updatedLinkProperties.settings,
									...result.linkProperties.settings,
								},
							}
						: updatedLinkProperties;

					const matchingLinks = [firstItem, appState.actions.getFeatureItemById<Link>(firstItem.id, XyiconFeature.Link)].filter(
						(link, index, array) => !!link && array.indexOf(link) === index,
					);

					for (const link of matchingLinks) {
						link.applyData?.({
							lastModifiedAt: result.lastModifiedAt ?? link.data.lastModifiedAt,
							lastModifiedBy: result.lastModifiedBy ?? link.data.lastModifiedBy,
							linkProperties: resolvedLinkProperties,
						});
					}

					if (result.linkProperties && !ObjectUtils.compare(result.linkProperties, updatedLinkProperties)) {
						console.warn("Link properties were updated by another user, reloading link properties.");
						spaceViewRenderer.spaceItemController.linkLineManager.updateLinkProperties(firstItem.id, resolvedLinkProperties);
						spaceViewRenderer.needsRender = true;
					}

					this.forceUpdate();
				}
			});
		} catch (error) {
			console.error("Failed to update link properties:", error);
		}
	};

	private readonly getFullName = (firstNameRaw?: string, lastNameRaw?: string): string => {
		return `${Formatter.capitalize(firstNameRaw ?? "")} ${Formatter.capitalize(lastNameRaw ?? "")}`.trim();
	};

	private readonly getLastModifiedDetails = (item: Link): ILastModifiedDetails => {
		const {lastModifiedBy, lastModifiedAt} = item.data;
		const users = this.props.appState.actions.getList<User>(XyiconFeature.User);
		const lastModifiedDisplayDate = DateFormatter.format(lastModifiedAt.toString(), "datetime");

		const lastModifiedByUser = users.find((user) => user.id === lastModifiedBy);

		if (!lastModifiedByUser) {
			const removedUsers = this.props.appState.removedUsersList;
			const removedUser = removedUsers.find((user) => user.userID === lastModifiedBy);

			if (removedUser) {
				const fullName = this.getFullName(removedUser.firstName, removedUser.lastName);

				return {
					lastModifiedByUser: fullName || "",
					lastModifiedAt: lastModifiedDisplayDate,
				};
			}
		}

		const fullName = this.getFullName(lastModifiedByUser?.data.firstName, lastModifiedByUser?.data.lastName);

		return {
			lastModifiedByUser: fullName || "",
			lastModifiedAt: lastModifiedDisplayDate,
		};
	};

	private readonly getCurrentLinkProperties = () => {
		const {items} = this.props;
		const firstItem = items[0] as IModel as Link;

		return firstItem?.data?.linkProperties || DetailsContentV5.DEFAULT_LINK_PROPERTIES;
	};

	private readonly headerControls = (items: IModel[]) => {
		const {appState, additionalHeaderControls, onCloseOverlayedDetaislPanel} = this.props;

		if (items.length !== 1) {
			return null;
		}

		const item = items[0];

		return (
			<>
				{doesUserHavePermissionsToSpaceOfItem(item, appState) && (
					<>
						<IconButtonV5
							onClick={this.onOpenInSpaceEditorClick}
							IconComponent={PinIcon}
							title="Open in Space Editor"
						/>
						{this.state.detailedBoundary === (item as Boundary) && (
							<BoundarySpaceMapSelectorV5
								item={item as Boundary}
								onClose={this.onHideBoundarySpaceMaps}
								style={{right: "60px", top: "48px", left: "initial"}}
							/>
						)}
					</>
				)}
				{item.ownFeature === XyiconFeature.Portfolio && (
					<ButtonV5
						onClick={() => openWorkspaceFromPortfolioModule(appState, item.id)}
						IconComponent={OpenPortfolioIcon}
						label="Open"
						type="secondary"
					/>
				)}
				{additionalHeaderControls && additionalHeaderControls(items)}

				{onCloseOverlayedDetaislPanel && (
					<IconButtonV5
						onClick={onCloseOverlayedDetaislPanel}
						IconComponent={CloseIcon}
						title="Close"
					/>
				)}
			</>
		);
	};

	public renderDefaultHeaderDiv(anchoredOne: boolean) {
		const {feature, noInitials, onFocus, onSelect} = this.props;
		const {headerIsAnchored, headerMinimized} = this.state;
		const {items} = this;
		const firstItem = items[0];
		let linkThumbnailAndRefIdDetails = null;
		let lastModifiedDetails = null;
		let currentLinkProperties = null;

		if (items.length === 1 && firstItem.ownFeature === XyiconFeature.Link) {
			linkThumbnailAndRefIdDetails = this.getLinkThumbnailAndRedIdDetails(firstItem as unknown as Link);
			lastModifiedDetails = this.getLastModifiedDetails(firstItem as unknown as Link);
			currentLinkProperties = this.getCurrentLinkProperties();
		}

		return (
			<DetailsContentV5HeaderStyled
				className={ReactUtils.cls({
					noInitials,
					[featureTitles[feature]]: true,
					visible: headerIsAnchored,
					anchored: anchoredOne,
					minimized: anchoredOne && headerMinimized && headerIsAnchored,
				})}
			>
				<FlexCenter $gap="10px">
					<InfoIcon />
					<h2>{this.getHeaderTitle()}</h2>
					{this.headerControls(items as IModel[])}
				</FlexCenter>
				{items.length === 1 ? (
					<Flex
						$gap={baseDistance.md}
						className="default-fields"
					>
						<VerticalFlex>{!noInitials && this.renderInitialComponent()}</VerticalFlex>
						<VerticalFlex
							$flex="1"
							$gap={(!headerIsAnchored || !headerMinimized) && baseDistance.sm}
						>
							{this.renderDefaultFields()}
						</VerticalFlex>
					</Flex>
				) : (
					!(anchoredOne && headerMinimized) && (
						<MultiDefaultFieldsV5
							items={items}
							onFocus={onFocus}
							onSelect={onSelect}
						/>
					)
				)}
				{anchoredOne && (
					<IconButtonV5
						className="toggler"
						IconComponent={AngleDown}
						onClick={() => this.setState((prevState) => ({headerMinimized: !prevState.headerMinimized}))}
					/>
				)}
				{items.length === 1 && [XyiconFeature.Link].includes(firstItem.ownFeature) && (
					<VerticalFlex $gap="16px">
						<LineDetailThumbnailContainerStyled>
							<VerticalFlex
								$gap="8px"
								style={{alignItems: "center"}}
							>
								<img
									src={linkThumbnailAndRefIdDetails.objectA.thumbnail}
									alt={linkThumbnailAndRefIdDetails.objectA.refId}
								/>
								<div className="refId">{linkThumbnailAndRefIdDetails.objectA.refId}</div>
							</VerticalFlex>
							<div
								className="directionalityIconContainer"
								key={`icon-${currentLinkProperties.direction}`}
							>
								{this.directionalityIconOptions.find((opt: {value: LinkDirection}) => opt.value === currentLinkProperties.direction)?.icon}
							</div>
							<VerticalFlex
								$gap="10px"
								style={{alignItems: "center"}}
							>
								<img
									src={linkThumbnailAndRefIdDetails.objectB.thumbnail}
									alt={linkThumbnailAndRefIdDetails.objectB.refId}
								/>
								<div className="refId">{linkThumbnailAndRefIdDetails.objectB.refId}</div>
							</VerticalFlex>
						</LineDetailThumbnailContainerStyled>
						<LineDetailContainerStyled>
							<VerticalFlex
								$gap="8px"
								style={{alignItems: "center"}}
							>
								<span className="lineDetailTitle">Directionality</span>
								<span className="lineDetailTitle">Last Updated By</span>
								<span className="lineDetailTitle">Last Updated</span>
							</VerticalFlex>
							<VerticalFlex $gap="8px">
								<SelectInputV5
									key={`directionality-${currentLinkProperties.direction}`}
									options={this.directionalityIconOptions}
									renderOptionWhenClosed={(option) => option.icon}
									selected={this.directionalityIconOptions.find((opt: {value: LinkDirection}) => opt.value === currentLinkProperties.direction)}
									onChange={(option) => option.onClick()}
									className="DirectionalityDropDown"
									dropdownFixedWidth="200px"
									sort={false}
								/>
								<span className="lineDetailValue">{lastModifiedDetails.lastModifiedByUser}</span>
								<span className="lineDetailValue">{lastModifiedDetails.lastModifiedAt}</span>
							</VerticalFlex>
						</LineDetailContainerStyled>
					</VerticalFlex>
				)}
			</DetailsContentV5HeaderStyled>
		);
	}

	private onScroll = () => {
		const {headerIsAnchored} = this.state;
		const limit = 0;

		if (this._ref.current.scrollTop > limit && !headerIsAnchored) {
			this.setState({headerIsAnchored: true});
		} else if (this._ref.current.scrollTop <= limit && headerIsAnchored) {
			this.setState({headerIsAnchored: false, headerMinimized: true});
		}
	};

	private getSpaces(): Space[] {
		const portfolio = this.props.items[0];

		if (this.props.items.length !== 1 || portfolio.ownFeature !== XyiconFeature.Portfolio) {
			return this.props.appState.actions.getList<Space>(XyiconFeature.Space);
		}

		const selectedPortfolio = portfolio as IModel as Portfolio;
		const spacesFromStore = this.props.appState.actions
			.getList<Space>(XyiconFeature.Space)
			.filter((space) => space.portfolioId === selectedPortfolio.id);

		// Fallback for legacy responses that still include embedded spaces on portfolio details.
		if (spacesFromStore.length === 0) {
			return (selectedPortfolio.spaces || []).map((spaceDto: SpaceDto) =>
				FeatureService.createModel(spaceDto, XyiconFeature.Space, this.props.appState),
			) as Space[];
		}

		return spacesFromStore;
	}

	public override async componentDidMount() {
		this._intersectionObserver.observe(this._refForIntersectionObserver.current as Element);
		window.addEventListener("hashchange", this.onHashChange);

		// Listen to SignalR link property updates to trigger re-render for the current link
		this._linkPropertiesUpdateHandler = (data: LinkBasicDto) => {
			const {items, feature} = this.props;
			if (feature === XyiconFeature.Link && items.length === 1) {
				const currentLink = items[0] as IModel as Link;

				if (currentLink?.id === data.linkID) {
					// Force re-render when this link's properties are updated via SignalR
					this.forceUpdate();
				}
			}
		};
		this.props.transport.signalR.listener.signals.linkPropertiesUpdated.add(this._linkPropertiesUpdateHandler);
		if (this.getSelectedPortfolioFromDetails()) {
			this.props.transport.services.feature.refreshList(XyiconFeature.Link);
			await extendLocalOrgSearchModel(this.props.appState, this.items[0]);
		}

		this.setState({isSpaceListLoading: false});

		this._ref.current?.addEventListener("scroll", this.onScroll);
		document.addEventListener("keydown", this.onKeyDown);
		document.addEventListener("mousedown", this.onMouseDown);
		document.addEventListener("click", this.onDocumentClick);
	}

	public override componentDidUpdate(prevProps: Readonly<IDetailsContentV5Props<T>>, prevState: Readonly<IDetailsContentV5State>): void {
		const prevItemIds = DetailsContentV5.getItemIds(prevProps.items);
		const newItemIds = DetailsContentV5.getItemIds(this.items);
		if (prevItemIds !== newItemIds) {
			if (this._ref.current?.parentElement?.scrollTop) {
				this._ref.current.parentElement.scrollTop = 0;
			}

			DebugInformation.end(logId);
		}
	}

	public override getSnapshotBeforeUpdate(prevProps: Readonly<IDetailsContentV5Props<T>>, prevState: Readonly<IDetailsContentV5State>): null {
		if (DetailsContentV5.getItemIds(prevProps.items) !== DetailsContentV5.getItemIds(this.items)) {
			this.blurActiveFieldInput();
		}

		return null;
	}

	public override componentWillUnmount() {
		this.blurActiveFieldInput();

		document.removeEventListener("keydown", this.onKeyDown);
		document.removeEventListener("click", this.onDocumentClick);
		document.removeEventListener("mousedown", this.onMouseDown);
		this._ref.current?.removeEventListener("scroll", this.onScroll);
		window.removeEventListener("hashchange", this.onHashChange);
		if (this._linkPropertiesUpdateHandler) {
			this.props.transport.signalR.listener.signals.linkPropertiesUpdated.remove(this._linkPropertiesUpdateHandler);
			this._linkPropertiesUpdateHandler = null;
		}

		this.props.appState.selectedFieldInputRefId = "";

		this._intersectionObserver.disconnect();
	}

	private blurActiveFieldInput() {
		const activeElement = document.activeElement;
		const isKeyboardInput =
			activeElement instanceof HTMLInputElement ||
			activeElement instanceof HTMLTextAreaElement ||
			(activeElement instanceof HTMLElement && activeElement.isContentEditable);

		if (activeElement instanceof HTMLElement && isKeyboardInput && this._ref.current?.contains(activeElement)) {
			activeElement.blur();
		}
	}

	public override render() {
		DebugInformation.start(logId);
		const {feature, appState} = this.props;
		const spaces = this.getSpaces();
		const {spaceViewRenderer} = appState.app;
		const {items} = this;
		const firstItem = items[0];
		const shouldShowSpaceLoader = this.state.isSpaceListLoading;
		const isAnySpaceTypeNotHidden = appState.actions.hasAnyTypeTheGivenPermissionInModule(XyiconFeature.Space, Permission.Update);
		const currentLinkProperties = this.getCurrentLinkProperties();
		const catalogFieldList = this.getAssignedList(XyiconFeature.XyiconCatalog);
		const xyiconFieldList = this.getAssignedList(XyiconFeature.Xyicon);

		return (
			<>
				<div
					ref={this._refForIntersectionObserver}
					style={{width: 1, height: 1}}
				></div>
				<DetailsContentV5Styled>
					<VerticalFlex
						$flex="1"
						className={ReactUtils.cls({multiSelect: items.length > 1})}
						ref={this._ref}
					>
						{items.length === 0 && <div className="noData">{"Please select one or more objects to display details."}</div>}
						{items.length > 0 && (
							<>
								{this.renderDefaultHeaderDiv(true)}
								{this.renderDefaultHeaderDiv(false)}
							</>
						)}
						{items.length === 1 &&
							[XyiconFeature.Link].includes(feature) &&
							(() => {
								const selectedLineStyle = this.lineStyleIconOptions.find(
									(option: {value: LineStyle}) => option.value === currentLinkProperties?.settings?.lineStyle,
								);
								const selectedLineWidth = this.lineWidthIconOptions.find(
									(option: {value: number}) => option.value === currentLinkProperties?.settings?.lineWidth,
								);
								const selectedArrowStyle = this.arrowStyleIconOptions.find(
									(option: {value: ArrowStyle}) => option.value === currentLinkProperties?.settings?.arrowStyle,
								);

								return (
									<ToggleContainerV5
										title="Line Style"
										saveStateToLocalStorage={true}
									>
										<LineStyleContainerStyled>
											<VerticalFlex $gap="2px">
												<span className="lineColorTitle">Color</span>
												<span className="lineStyleTitle"> Line Style</span>
												<span className="lineWidthTitle">Line Width</span>
												<span className="arrowStyleTitle">Arrow Style</span>
											</VerticalFlex>
											<VerticalFlex $gap="2px">
												<ColorSelectorV5
													title="Color"
													color={{hex: currentLinkProperties?.settings?.lineColor, transparency: 0}}
													onColorChange={(color) => {
														this.updateLinkProperties({settings: {lineColor: `#${color.hex}`}});
													}}
													isTransparencyEnabled={false}
													horizontalAlignment={HorizontalAlignment.outerRight}
												/>
												<SelectInputV5
													options={this.lineStyleIconOptions}
													renderOptionWhenClosed={(option) => option.icon}
													selected={selectedLineStyle}
													onChange={(option) => option.onClick()}
													isSameWidth={true}
													className="LineStyleDropDown"
												/>
												<SelectInputV5
													options={this.lineWidthIconOptions}
													renderOptionWhenClosed={(option) => option.label}
													selected={selectedLineWidth}
													onChange={(option) => option.onClick()}
													isSameWidth={true}
													dropdownMaxHeight="176px"
													className="LineWidthDropDown"
												/>
												<SelectInputV5
													options={this.arrowStyleIconOptions}
													renderOptionWhenClosed={(option) => option.icon}
													selected={selectedArrowStyle}
													onChange={(option) => option.onClick()}
													isSameWidth={true}
													className="ArrowStyleDropDown"
												/>
											</VerticalFlex>
										</LineStyleContainerStyled>
									</ToggleContainerV5>
								);
							})()}
						{items.length > 0 && ![XyiconFeature.Report, XyiconFeature.Markup, XyiconFeature.Link].includes(feature) && (
							<ToggleContainerV5
								title="Fields"
								saveStateToLocalStorage={true}
							>
								{this.renderFields()}
							</ToggleContainerV5>
						)}
						{items.length === 1 && catalogFieldList.length > 0 && (
							<ToggleContainerV5
								title="Assign Fields by Catalog Model"
								saveStateToLocalStorage={true}
							>
								<SelectSliderV5
									options={this._assignedTypes}
									rows={this.getSliderRowsFromFieldList(catalogFieldList, items, XyiconFeature.XyiconCatalog)}
									onChange={(fieldNames: string[], value: AssignType) => this.onSliderChange(fieldNames, value, XyiconFeature.XyiconCatalog)}
								/>
							</ToggleContainerV5>
						)}
						{items.length === 1 && xyiconFieldList.length > 0 && (
							<ToggleContainerV5
								title="Assign Fields by Xyicon Model"
								saveStateToLocalStorage={true}
							>
								<SelectSliderV5
									options={this._assignedTypes}
									rows={this.getSliderRowsFromFieldList(xyiconFieldList, items, XyiconFeature.Xyicon)}
									onChange={(fieldNames: string[], value: AssignType) => this.onSliderChange(fieldNames, value, XyiconFeature.Xyicon)}
								></SelectSliderV5>
							</ToggleContainerV5>
						)}
						{items.length === 1 && (
							<>
								{feature === XyiconFeature.Portfolio && (
									<ToggleContainerV5
										title="Spaces"
										saveStateToLocalStorage={true}
									>
										{shouldShowSpaceLoader ? (
											<div className="loadingSpaces">
												<Spinner isVisible={true} />
												<span className="loadText">Loading Spaces...</span>
											</div>
										) : spaces.length === 0 ? (
											<div className="noDataSection">
												<EmptyListViewV5
													onAddClick={isAnySpaceTypeNotHidden && this.onAddSpaceClick}
													feature={XyiconFeature.Space}
													isDetailsContent={true}
												/>
											</div>
										) : (
											<SpaceThumbnailsStyled>
												{spaces
													.toSorted((a: Space, b: Space) => StringUtils.sortIgnoreCase(a.name, b.name))
													.map((space: Space) => {
														return (
															<SpaceCard
																key={space.id}
																isSelected={false}
																space={space}
																onClick={this.onSpaceClick}
															/>
														);
													})}
											</SpaceThumbnailsStyled>
										)}
									</ToggleContainerV5>
								)}
								<LinksSectionV5
									item={firstItem}
									feature={feature}
									saveStateToLocalStorage={true}
								/>
								{feature !== XyiconFeature.Report && (
									<PortsSectionV5
										item={firstItem}
										feature={feature}
										isPortTemplateEditorOpen={this.props.isPortTemplateEditorOpen}
										setPortTemplateEditorOpen={this.props.setPortTemplateEditorOpen}
										saveStateToLocalStorage={true}
									/>
								)}
							</>
						)}
						{items.length > 0 && (
							<DocumentSectionV5
								items={items}
								feature={feature}
								saveStateToLocalStorage={true}
							/>
						)}
						{items.length === 1 && feature === XyiconFeature.Report && (
							<>
								<ToggleContainerV5
									title="Scope"
									open={true}
								>
									<ReportScopeWidgetV5
										report={firstItem as IModel as Report}
										disabled={true}
										isDetailsPanel={true}
									/>
								</ToggleContainerV5>

								{this.isReportEditable && (
									<ToggleContainerV5
										title="Sharing"
										open={true}
									>
										<ReportSharingContainer report={items[0] as unknown as Report} />
									</ToggleContainerV5>
								)}

								<ToggleContainerV5
									title="Recently Generated Reports"
									open={true}
									saveStateToLocalStorage={true}
								>
									<ReportHistoryV5 report={firstItem as IModel as Report} />
								</ToggleContainerV5>
							</>
						)}
						{items.length > 0 &&
							spaceViewRenderer.isMounted &&
							items.every(
								(item) =>
									(item.ownFeature === XyiconFeature.Xyicon && !(item as IModel as Xyicon).isEmbedded) ||
									item.ownFeature === XyiconFeature.Boundary ||
									item.ownFeature === XyiconFeature.Markup,
							) && (
								<PropertiesSectionV5
									saveStateToLocalStorage={true}
									properties={this.getProperties(firstItem as IModel as Xyicon)}
									items={items}
								/>
							)}
					</VerticalFlex>
				</DetailsContentV5Styled>
				{this.state.createViewPopupType && (
					<CreateViewPopup
						onClose={this.closeCreateViewPopup}
						onCreateCallback={this.onLocateSelectedItemInSpaceEditor}
						type={this.state.createViewPopupType}
						defaultTemplateFeature={XyiconFeature.SpaceEditor}
						viewFolderIdToSaveNewViewInto={""}
					/>
				)}
			</>
		);
	}
}

const DetailsContentV5Styled = styled.div`
	${VerticalFlexStyle};
	flex: 1;
	min-width: 0;
	padding: ${baseDistance.md};

	.loadingSpaces {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: ${baseDistance.sm};
	}

	${ToggleContainerStyled} {
		.children.open {
			padding-left: ${baseDistance.md};
			padding-right: 0;

			.lookup ${MultiSelectInputStyled} {
				border-radius: ${baseDistance.xs};
				cursor: pointer;
				min-height: 32px;
				${ELLIPSIS}

				&:hover {
					background-color: ${colorPalette.gray.c200Light};
				}

				&:focus {
					background-color: ${colorPalette.white};
					border: 1px solid ${colorPalette.primary.c500Primary};
				}

				&.isActive {
					background-color: ${colorPalette.white};
					border: 1px solid ${colorPalette.primary.c500Primary}!important; //Field.styled.ts TODO bad rule causes this
				}
			}

			${ToggleContainerStyled} {
				.children.open {
					padding-left: 0;
					margin-left: 0px;
				}
			}

			.children.open ${FieldElementsContainerStyled} .element {
				margin-right: 0;
			}

			${PropagatedFieldInDetailsContent} {
				.SingleLineLabel {
					width: calc(100% - 33px);
					color: ${colorPalette.gray.c700Dark};
				}

				.buttonContainer {
					padding-right: ${baseDistance.xs};

					${IconButtonStyled} {
						height: 24px;
						min-width: 24px;

						svg {
							height: 16px;
							width: 16px;
						}
					}
				}
			}
		}
	}
`;

const SpaceThumbnailsStyled = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(268px, 1fr));
	gap: ${baseDistance.md};
`;

const LineDetailThumbnailContainerStyled = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: center;
	margin-bottom: ${baseDistance.md};
	gap: ${baseDistance.md};
	img {
		width: 40px;
		height: 40px;
	}

	.directionalityIconContainer {
		margin-bottom: 26px;
		min-width: 18px;
	}

	.refId {
		font-size: 14px;
		font-weight: ${fontWeight.normal};
		color: #363636;
		line-height: 16px;
	}
`;

const LineDetailContainerStyled = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-between;

	.lineDetailTitle {
		display: flex;
		align-items: center;
		font-size: 14px;
		font-weight: ${fontWeight.normal};
		color: #363636;
		line-height: 16px;
		height: 32px;
		min-width: 120px;
		max-width: 160px;
	}
	.lineDetailValue {
		display: flex;
		align-items: center;
		font-size: 14px;
		font-weight: ${fontWeight.normal};
		color: #7b7b7b;
		line-height: 16px;
		height: 32px;
		width: 200px;
	}

	.DirectionalityDropDown {
		span {
			margin-left: ${baseDistance.sm};
		}

		svg {
			width: 16px;
			height: 16px;
		}
	}
`;

const LineStyleContainerStyled = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: calc(${baseDistance.md} * 3);
	margin-top: ${baseDistance.sm};

	.LineWidthDropDown,
	.LineStyleDropDown,
	.ArrowStyleDropDown {
		width: 114px;
	}

	.LineWidthDropDown {
		span {
			margin-left: 8px;
		}
	}

	.lineWidthLabel,
	.lineStyleLabel {
		font-size: ${fontSize.md};
		font-weight: ${fontWeight.normal};
	}

	.lineColorTitle,
	.lineStyleTitle,
	.lineWidthTitle,
	.arrowStyleTitle {
		font-size: ${fontSize.md};
		font-weight: ${fontWeight.normal};
		color: #363636;
		height: 32px;
		line-height: 16px;
		display: flex;
		align-items: center;
		min-width: 120px;
	}
	.lineDetailValue {
		font-size: ${fontSize.md};
		font-weight: ${fontWeight.normal};
		color: #7b7b7b;
		line-height: 16px;
		height: 32px;
	}

	svg {
		width: 32px;
		height: 32px;
	}
`;

const LineStyleOptionsStyled = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: ${baseDistance.sm};

	span {
		font-size: ${fontSize.md};
		font-weight: ${fontWeight.normal};
	}

	svg {
		width: 16px;
		height: 16px;
	}
`;
